// Issue slideover — the maintenance tracker's issue page: the header band,
// the report and its photos, the issue's sections, and Go to · Note ·
// Summary over What's happened. Reads /issues/:id.json.
import flatpickr from 'flatpickr'
import { notesMixin, destroyNote } from './notes_mixin'
import * as contactGroup from '../utils/contact_group'

// Who can authorise: the landlord, or us on the property manager's own authority
const AUTHORISED_BY_OPTIONS = [
  { value: 'landlord', label: 'The landlord' },
  { value: 'us', label: "Us, on the property manager's own authority" }
]

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

const today = () => new Date().toISOString().slice(0, 10)
const daysFromNow = (n) => new Date(Date.now() + n * 86400000).toISOString().slice(0, 10)

const csrf = () => document.querySelector('meta[name="csrf-token"]')?.content

export function issueSlideover() {
  return {
    ...notesMixin,

    open: false,
    zIndex: 100010,
    issueId: null,
    issue: null,
    pendingVerb: null,
    freshLoad: false,
    loading: false,
    error: null,
    goToOpen: false,
    noteOpen: false,
    // The carets and folds open on the page, by key
    openKeys: {},
    // Close issue was refused: the banner says what is in the way
    closeRefused: false,
    photosUploading: false,

    aiWorking: false,
    aiWorkingLabel: '',
    summarySections: null,
    summaryMeta: { events: 0, records: 0 },

    noteForm: { content: '', saving: false },
    lightbox: { open: false, index: 0 },

    // The kebab's and the rail's modals share one state: which is open, the
    // job it concerns, and the form
    modal: { open: null },
    modalJob: null,
    modalQuote: null,
    form: {},
    options: (() => { try { return JSON.parse(document.querySelector('[data-issue-options]')?.dataset.issueOptions || '{}') } catch (e) { return {} } })(),

    // A compliance renewal is an issue; opened by its visit's id (from the
    // compliance pages) it is resolved to that issue first, then everything
    // posts under /issues like any other
    renewal: false,
    openedByVisit: null,
    // The card a doorway came from, carried on the save that answers it
    proposedActionId: null,
    pendingDoorway: null,
    get base() { return `/issues/${this.issueId}` },
    get workBase() { return this.base },
    jobBase(_job) { return this.base },
    get noteSubject() { return { type: 'Issue', id: this.issueId } },

    authModal: { open: false, by: 'landlord', note: '', changing: false, saving: false, error: null },

    authorisedByOptions: AUTHORISED_BY_OPTIONS,

    // --- Open / close -------------------------------------------------------

    // A verb from the Maintenance page's row (ask, chase, invite, complete,
    // email_contractor, send_quote) runs once the issue has loaded. A
    // doorway (Alice's card, the inbox's filing chip) arrives with a
    // prefill and the card's id, and opens its modal the same way.
    openForIssue(id, verb = null, renewal = false, doorway = null) {
      this.renewal = !!renewal
      this.openedByVisit = renewal ? id : null
      const store = Alpine.store('slideovers')
      if (!renewal && store?.isOpen?.('issue', id)) { store.bringToFront('issue', id); if (verb) this.runRowVerb(verb); if (doorway) this.openDoorway(doorway); return }
      this.pendingVerb = verb
      this.pendingDoorway = doorway
      this.zIndex = store?.getNextZIndex ? store.getNextZIndex() : this.zIndex
      this.issueId = id
      this.issue = null
      this.error = null
      this.goToOpen = false
      this.noteOpen = false
      this.closeRefused = false
      this.openKeys = {}
      this.summarySections = null
      this.aiWorking = false
      this.open = true
      store?.register?.('issue', id, this)
      this.fetchIssue().then(() => {
        if (!this.issue) return
        if (this.pendingVerb) { const v = this.pendingVerb; this.pendingVerb = null; this.runRowVerb(v) }
        if (this.pendingDoorway) { const d = this.pendingDoorway; this.pendingDoorway = null; this.openDoorway(d) }
      })
    },

    // A doorway's prefill: the invoice or quote modal filled from what was
    // read off the email, with the emailed file standing in for an upload;
    // or the approval email for the quote the landlord said yes to
    openDoorway(d) {
      this.proposedActionId = d.proposed_action_id || null
      const contractorFor = (id, name) => {
        if (!id) return null
        return this.suggestedContractors().find(c => c.id === Number(id)) || { id: Number(id), display_name: name || 'Contractor', name: name || 'Contractor' }
      }
      if (d.invoice_prefill) {
        const p = d.invoice_prefill
        this.openModal('invoice')
        Object.assign(this.form, {
          contractor: contractorFor(p.contractor_id, p.contractor_name) || this.form.contractor,
          gross_amount: p.amount || this.form.gross_amount, invoice_reference: p.reference || '', invoice_date: p.date || this.form.invoice_date,
          description: p.description || this.form.description, record_only: p.charge_mode === 'record_only',
          email_attachment_id: p.email_attachment?.id || null, file: p.email_attachment ? { name: p.email_attachment.filename } : null
        })
      } else if (d.quote_prefill) {
        const p = d.quote_prefill
        this.openModal('quote')
        Object.assign(this.form, {
          contractor: contractorFor(p.contractor_id, p.contractor_name) || this.form.contractor,
          reference: p.reference || '', issued_at: p.issued_at || '', valid_until: p.valid_until || '', notes: p.notes || '',
          lines: Array.isArray(p.lines) && p.lines.length ? this.readLines(p.lines) : this.form.lines, vat: parseFloat(p.vat_rate) > 0 || this.form.vat,
          email_attachment_id: p.email_attachment?.id || null, file: p.email_attachment ? { name: p.email_attachment.filename } : null,
          email_thread_id: p.email_thread_id || null
        })
      } else if (d.approve_prefill) {
        const q = (this.issue.quotes || []).find(q => ['received', 'with_landlord'].includes(q.status) && q.contractor?.id === Number(d.approve_prefill.contractor_id))
        if (q) this.approveQuote(q)
      }
    },

    runRowVerb(verb) {
      const job = this.liveJob
      switch (verb) {
        case 'ask': return this.askByEmail()
        case 'log_answer': return this.openAuthorisationModal()
        case 'schedule': return job && this.openModal('book', job)
        case 'quote': {
          // A price awaited from a visit is that visit's quote
          const visit = this.asks.find(j => j.price_to_follow)
          return visit ? this.addQuoteFor(visit) : this.openModal('quote')
        }
        case 'invoice': return this.openModal('invoice')
        case 'chase': return job && this.chaseJob(job)
        case 'invite': return this.openModal('invite')
        case 'complete': return this.openModal('complete', job)
        // After a request's visit the next thing is their quote
        case 'outcome': return job ? this.addQuoteFor(job) : this.openModal('quote')
        case 'email_contractor': {
          const contractor = (this.asks.find(j => j.price_to_follow) || job)?.contractor
          return contractor?.email && this.emailContact({ ...contractor, type: 'Contractor' })
        }
        case 'send_quote': return this.sendQuotesToLandlords()
        default: return null
      }
    },

    init() {
      // A send from the composer that completed against this issue (a quote to
      // the landlords, an approval, an ask) comes back as linked-send-completed
      window.addEventListener('linked-send-completed', (e) => {
        if (this.open && e.detail?.issue?.id === this.issueId) this.issue = e.detail.issue
      })
      // Every change to the issue after its first load is news for the page
      // behind (the Maintenance list re-reads its rows)
      this.$watch('issue', (v) => {
        if (!v) return
        if (this.freshLoad) { this.freshLoad = false; return }
        window.dispatchEvent(new CustomEvent('issue-changed', { detail: { id: v.id } }))
      })
    },

    close() {
      this.open = false
      this.goToOpen = false
      Alpine.store('slideovers')?.unregister?.('issue', this.issueId)
    },

    async fetchIssue(silent = false) {
      if (!silent) this.loading = true
      this.error = null
      try {
        // Opened by a visit's id, the renewal is fetched that way once; its issue id is what the panel keeps
        const url = this.openedByVisit ? `/renewals/${this.openedByVisit}.json` : `${this.base}.json`
        const response = await fetch(url, {
          headers: { 'Accept': 'application/json', 'X-CSRF-Token': csrf() }
        })
        if (!response.ok) throw new Error(`HTTP ${response.status}`)
        this.freshLoad = true
        const data = await response.json()
        if (data.id !== this.issueId) {
          Alpine.store('slideovers')?.unregister?.('issue', this.issueId)
          this.issueId = data.id
          Alpine.store('slideovers')?.register?.('issue', data.id, this)
        }
        this.openedByVisit = null
        this.renewal = !!data.renewal
        this.issue = data
      } catch (e) {
        console.error('Issue load failed:', e)
        this.error = 'Could not load this issue'
      } finally {
        this.loading = false
      }
    },

    // --- Derived -----------------------------------------------------------

    // Everyone asked onto the issue: its work orders and quote requests, oldest first
    get asks() {
      return [...(this.issue?.work_orders || []), ...(this.issue?.quote_requests || [])].sort((a, b) => (a.created_at || '').localeCompare(b.created_at || ''))
    },

    get liveJob() {
      const live = this.issue?.live_ask
      if (!live) return null
      return this.asks.find(j => j.kind === live.kind && j.id === live.id) || null
    },

    // The verbs on a work order or a quote request live under its own path
    askPath(job) { return `${this.jobBase(job)}/${job.kind === 'quote_request' ? 'quote_requests' : 'work_orders'}/${job.id}` },

    // --- The pieces of work ------------------------------------------------

    // The jobs still open on the issue: what a new ask, price or instruction can be for
    openJobs() { return (this.issue?.issue_jobs || []).filter(w => w.status === 'open') },
    workById(id) { return (this.issue?.issue_jobs || []).find(w => String(w.id) === String(id)) || null },
    // The picker's default: the one open job, else a new piece of work
    defaultJobChoice() { const open = this.openJobs(); return open.length === 1 ? String(open[0].id) : '__new' },
    // The job the form is for, or null while it is a new one still to be described
    chosenWork() { return this.form.issue_job_id && this.form.issue_job_id !== '__new' ? this.workById(this.form.issue_job_id) : null },
    jobChoiceReady() { return this.form.issue_job_id !== '__new' && !!this.form.issue_job_id ? true : !!(this.form.job_title || '').trim() },
    // Instructing the work on a job that already has a live work order: a second one needs an explicit yes
    secondOrderWarning() {
      const w = this.chosenWork()
      if (!w || !w.live_order) return null
      if (this.modal.open === 'invite' && this.form.kind !== 'work_order') return null
      if (this.modal.open === 'approve' && this.form.as_change) return null
      return w.live_order
    },
    // The other quotes and requests still open on the same job as a quote being accepted
    rivalsFor(q) {
      const quotes = (this.issue?.quotes || []).filter(x => x.id !== q.id && x.issue_job_id === q.issue_job_id && ['received', 'with_landlord'].includes(x.status))
      const requests = this.asks.filter(j => j.issue_job_id === q.issue_job_id && j.kind === 'quote_request' && !j.dead && j.status !== 'completed' && j.contractor?.id !== q.contractor?.id)
      return { quotes, requests }
    },
    // When the work should start, as the form said: one of the four, the others empty
    startParams(f) {
      return { waits_for_job_id: f.start === 'after' ? f.waits_for_job_id || null : null, not_before: f.start === 'not_before' ? f.not_before || null : null, deadline: f.start === 'deadline' ? f.deadline || null : null }
    },
    // The other open jobs a new work order could wait for
    otherOpenJobs(excludeId) { return this.openJobs().filter(w => String(w.id) !== String(excludeId)) },
    // A job heading's own buttons: the modal opens already set to that job
    openModalForJob(name, issueJobId) {
      this.openModal(name)
      this.form.issue_job_id = String(issueJobId)
      if (name === 'invite') this.setKind(this.form.kind)
    },

    // "Reported by the tenant on 1 Oct 2026", a named reporter by name
    get reportedLine() {
      const i = this.issue
      if (!i) return ''
      const via = (i.reported_via_options || []).find(o => o.value === i.reported_via)
      const who = i.reported_via === 'inspection' && i.inspection?.found_by
        ? i.inspection.found_by
        : (i.reported_by?.name ? i.reported_by.name + (via ? ', ' + via.label.toLowerCase() + ',' : '') : (via ? via.label.toLowerCase() : 'someone'))
      const when = i.reported_via === 'inspection' && i.inspection?.found_by ? '' : ' on ' + this.shortDate(i.reported_at)
      return 'Reported by ' + who + when
    },
    // " expiring Thu 22 Oct 2026" after the certificate a renewal renews
    renewalExpiryWords() {
      const exp = this.issue?.renewal?.expiry_date
      return exp ? (this.dayDiff(exp) < 0 ? ' that expired ' : ' expiring ') + this.dayWords(exp) : ''
    },

    // The certificate a renewal renews, in words: "gas safety certificate", "EICR"
    certificateWords() {
      const name = this.issue?.renewal?.certificate_type || 'certificate'
      if (/eicr/i.test(name)) return 'EICR'
      return /certificate/i.test(name) ? name.toLowerCase() : name.toLowerCase() + ' certificate'
    },

    // The state badge: open, on hold, closed
    get headerPill() {
      const i = this.issue
      if (!i) return { label: '', tone: '' }
      if (i.status === 'closed') return { label: 'Closed', tone: 'ok' }
      if (i.status === 'cancelled') return { label: 'Cancelled', tone: '' }
      if (i.hold) return { label: 'On hold', tone: 'hold' }
      return { label: 'Open', tone: 'accent' }
    },

    // --- Words for dates and money -----------------------------------------

    // The tracker's colours: amber waiting on us, blue on someone else, green settled, purple hold
    pillClass(tone) {
      return { warn: 'bg-[#fef9c3] text-[#854d0e]', ok: 'bg-[#dcfce7] text-[#166534]', hold: 'bg-[#f3e8ff] text-[#6b21a8]', info: 'bg-[#dbeafe] text-[#1e40af]', accent: 'bg-brand-50 text-brand-500' }[tone] || 'bg-gray-100 text-gray-700'
    },
    // A date or a time as "1 Oct 2026", spelt as the server spells it (Sep, not Sept)
    shortDate(iso) {
      if (!iso) return ''
      const d = /^\d{4}-\d{2}-\d{2}$/.test(iso) ? new Date(iso + 'T12:00:00') : new Date(iso)
      return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`
    },
    // A day as "Wed 14 Oct 2026"
    dayWords(ymd) {
      if (!ymd) return ''
      const d = new Date(String(ymd).slice(0, 10) + 'T12:00:00')
      return `${WEEKDAYS[d.getDay()]} ${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`
    },
    dayDiff(ymd) {
      const t = new Date(); t.setHours(12, 0, 0, 0)
      return Math.round((new Date(String(ymd).slice(0, 10) + 'T12:00:00') - t) / 86400000)
    },
    relDay(ymd) {
      const n = this.dayDiff(ymd)
      return n === 0 ? 'today' : n === 1 ? 'tomorrow' : n === -1 ? 'yesterday' : n > 0 ? `in ${n} days` : `${-n} days ago`
    },
    money(n) {
      n = +n || 0
      return '£' + n.toLocaleString('en-GB', { minimumFractionDigits: n % 1 ? 2 : 0, maximumFractionDigits: 2 })
    },
    listWords(a) { return a.length <= 1 ? a.join('') : a.slice(0, -1).join(', ') + ' and ' + a[a.length - 1] },

    get eventsReversed() {
      return [...(this.issue?.events || [])].reverse()
    },

    // The one line on an ask's email arrives a moment after the send
    // (AskEmailSummaryJob); it goes on the ask's row as it lands
    askSummaryWritten(message) {
      if (!this.open || Number(message?.data?.issue_id) !== Number(this.issueId)) return
      const row = this.issue?.events?.find(e => e.id === `ask_${message.id}`)
      if (row) row.email_summary = message.data.email_summary
      const ask = this.issue?.asks?.find(a => a.id === message.id)
      if (ask) ask.email_summary = message.data.email_summary
    },

    // The issue's own files: the old jobs' files came across with them at
    // the cutover, so one already shown on an invoice or quote stays there
    get issueFiles() {
      const onCards = new Set([...this.invoiceRows.map(i => i.document?.blob_id), ...(this.issue?.quotes || []).map(q => q.document_blob_id)].filter(Boolean))
      return (this.issue?.documents || []).filter(d => !onCards.has(d.blob_id))
    },
    get imageDocuments() {
      return this.issueFiles.filter(d => d.content_type?.startsWith('image/'))
    },

    get goToGroups() {
      const links = this.issue?.entity_links || []
      const of = (type) => links.filter(l => l.type === type)
      const contactGroup = (key, rows, one, many) => {
        const emails = rows.map(r => r.email).filter(Boolean)
        return { key, label: rows.length > 1 ? `${many} · ${rows.length}` : one, rows, emails,
                 bulk: emails.length > 1 ? (emails.length === 2 ? 'Email both' : 'Email all') : null }
      }
      const groups = []
      const properties = of('Property')
      if (properties.length) groups.push({ key: 'property', label: 'Property', rows: properties })
      const landlords = of('Landlord'); if (landlords.length) groups.push(contactGroup('landlord', landlords, 'Landlord', 'Landlords'))
      const tenants = of('Person'); if (tenants.length) groups.push(contactGroup('person', tenants, 'Tenant', 'Tenants'))
      const contractors = of('Contractor'); if (contractors.length) groups.push(contactGroup('contractor', contractors, 'Contractor', 'Contractors'))
      return groups
    },

    // The contact rows and group headings shared with the tenancy and job
    // slideovers — utils/contact_group.js.
    getInitials(name) { return this.initials(name) },
    contactGroupLabel(contacts, one, many) { return contactGroup.groupLabel(contacts, one, many) },
    bulkEmailLabel(contacts) { return contactGroup.bulkEmailLabel(contacts) },
    emailGroup(contactsOrGroup, type, subject) {
      // The Go to panel passes its own group; the headings pass an array.
      if (Array.isArray(contactsOrGroup)) return contactGroup.emailGroup(contactsOrGroup, type, subject)
      if (typeof window.openEmail === 'function') window.openEmail(contactsOrGroup.emails, {})
      else window.location.href = `mailto:${contactsOrGroup.emails.join(',')}`
    },

    initials(name) {
      return (name || '').replace(/\(.*?\)/g, '').split(/\s+/).filter(w => w && !['Mr', 'Mrs', 'Ms', 'Miss', 'Dr', 'and', '&'].includes(w)).slice(0, 2).map(w => w[0]).join('').toUpperCase() || '?'
    },

    // --- Contact --------------------------------------------------------------

    callContact(c) {
      if (!c?.phone) return
      if (typeof window.openPhone === 'function') window.openPhone(c.phone)
      else window.location.href = `tel:${c.phone}`
    },

    emailContact(c) {
      if (!c?.email) return
      if (typeof window.openEmail === 'function') window.openEmail(c.email, { name: c.name, type: (c.type || '').toLowerCase(), id: c.id || c.entity_id })
      else window.location.href = `mailto:${c.email}`
    },

    // --- The landlord loop --------------------------------------------------

    // Ask by email: the draft opens in the composer with the landlords
    // addressed and every issue from the same inspection quoted. Sending is
    // what records the ask and links the reply back here.
    async askByEmail() {
      try {
        const params = new URLSearchParams()
        ;(this.issue?.sibling_issues || []).forEach(s => params.append('issue_ids[]', s.id))
        const response = await fetch(`${this.base}/landlord_email_draft?${params}`, { headers: { 'Accept': 'application/json' } })
        const data = await response.json().catch(() => ({}))
        if (!response.ok || !data.draft) throw new Error(data.error || 'Could not draft the email')
        const draft = data.draft
        // A doorway's card id rides on the completion, so the send resolves it
        const completion = this.proposedActionId && draft.completion ? { ...draft.completion, payload: { ...(draft.completion.payload || {}), proposed_action_id: this.proposedActionId } } : draft.completion
        this.proposedActionId = null
        window.dispatchEvent(new CustomEvent('open-email-compose', {
          detail: { to: draft.recipients, subject: draft.subject, body_html: draft.body_html, attachments: draft.attachments || [], completion, about: draft.about }
        }))
      } catch (e) {
        window.alert?.(e.message)
      }
    },

    // Record, or change, who authorised us to act on the issue
    openAuthorisationModal() {
      const a = this.issue?.authorisation
      this.authModal = { open: true, by: a?.by || 'landlord', note: a?.note || '', changing: !!a, saving: false, error: null }
    },

    // The landlords' general ask about the issue (not one about a quote): the one the card's chase and email are about
    get landlordAsk() {
      const id = (this.issue?.stages || []).find(s => s.key === 'landlords')?.ask_id
      return (this.issue?.asks || []).find(a => a.id === id) || null
    },

    // The Authorisation card's words: who authorised it, or where getting it stands
    get authCard() {
      const i = this.issue
      if (!i) return { heading: '', facts: [] }
      const a = i.authorisation
      if (a) return { heading: a.by === 'landlord' ? 'By the landlord' : "By us, on the property manager's own authority", facts: [a.at ? this.shortDate(a.at) : null, a.note].filter(Boolean) }
      if (i.landlord_told_at) {
        const ask = this.landlordAsk
        return { heading: 'Landlord told ' + this.shortDate(i.landlord_told_at),
                 facts: ['Nothing recorded yet', ask?.asked_again_display ? 'Asked again ' + ask.asked_again_display : null, ask?.chase_at && !ask.answered && this.isActive ? 'Ask again ' + this.shortDate(ask.chase_at) : null].filter(Boolean) }
      }
      return { heading: 'Landlord not told yet', facts: ['Nobody has authorised us to act on this issue yet'] }
    },

    async submitAuthorisation() {
      this.authModal.saving = true
      this.authModal.error = null
      try {
        const data = await this.post(`${this.workBase}/authorisation`, { by: this.authModal.by, note: this.authModal.note })
        if (data?.issue) this.issue = data.issue
        this.authModal.open = false
      } catch (e) {
        this.authModal.error = e.message
      } finally {
        this.authModal.saving = false
      }
    },

    // --- Summary, on demand -----------------------------------------------

    async summarise() {
      if (this.aiWorking || !this.issueId) return
      this.summarySections = null
      this.aiWorking = true
      this.aiWorkingLabel = 'Reading the issue, its emails and its records…'
      this.$nextTick(() => this.$refs.aiWorkingPanel?.scrollIntoView({ behavior: 'smooth', block: 'start' }))
      try {
        const response = await fetch(`${this.base}/summarise`, {
          method: 'POST',
          headers: { 'X-CSRF-Token': csrf(), 'Accept': 'application/json', 'Content-Type': 'application/json' }
        })
        const data = await response.json().catch(() => ({}))
        if (response.ok && data.success) {
          this.summarySections = data.sections
          this.summaryMeta = { events: data.event_count, records: data.record_count }
          this.$nextTick(() => this.$refs.summaryPanel?.scrollIntoView({ behavior: 'smooth', block: 'start' }))
        } else {
          this.error = data.error || 'Could not summarise this issue'
        }
      } catch (e) {
        console.error('Summarise failed:', e)
        this.error = 'Could not summarise this issue'
      } finally {
        this.aiWorking = false
      }
    },

    // --- Notes ---------------------------------------------------------------

    async addNote() {
      if (!this.noteForm.content?.trim() || this.noteForm.saving) return
      this.noteForm.saving = true
      try {
        const entry = await this.saveNote(this.noteSubject, this.noteForm.content)
        if (entry) {
          this.issue.events = [...this.issue.events, entry].sort((a, b) => (a.occurred_at || '').localeCompare(b.occurred_at || ''))
          this.noteForm.content = ''
          this.noteOpen = false
        }
      } catch (e) {
        console.error('Note failed:', e)
        alert(e.message || 'Could not add the note. Please try again.')
      } finally {
        this.noteForm.saving = false
      }
    },

    async deleteNote(entryId) {
      try {
        await destroyNote(entryId)
        this.removeTimelineEntry(entryId)
      } catch (e) {
        console.error('Delete note failed:', e)
      }
    },

    removeTimelineEntry(id) {
      if (!this.issue) return
      this.issue.events = this.issue.events.filter(e => e.id !== id)
    },

    // --- The verbs: modals ----------------------------------------------------

    get isActive() {
      return !!this.issue && this.issue.status !== 'closed' && this.issue.status !== 'cancelled'
    },

    // The brief is the user's own, written from scratch in the box, and the
    // email's message word for word: nothing is added to it on the way out.
    // These are the sentences briefs used to close with, by ask: a quote,
    // or the work. A card's Notes leave them out of older briefs. A quote
    // is what attending to advise always was (they look, or price from the
    // photos, and say what it needs); the job keeps its old key,
    // attend_and_advise.
    ASK_LINES: {
      quote_request: 'Can you please attend the property and provide a quote for what needs to be done to fix this problem.',
      work_order: 'Can you please attend the property and carry out the necessary work in order to fix this problem.'
    },
    // The attend-and-advise closing line of briefs written before the two
    // asks were one, left out of a card's Notes like the others
    RETIRED_ASK_LINES: [
      'Can you please attend the property and advise us what needs to be done in order to fix this problem.'
    ],

    // A quote request, or a work order: the same form
    setKind(kind) { this.form.kind = kind },

    // A renewal's own visit, waiting for its contractor: creating a work order instructs it
    get draftVisit() { return this.renewal ? this.asks.find(j => j.status === 'draft') || null : null },
    instructingDraft() { return this.modal.open === 'invite' && this.form.kind === 'work_order' && !!this.draftVisit },

    // Close issue: refused while a work order is live, or a renewal has no
    // satisfactory certificate; the banner says so. Otherwise the reason is asked
    closeIssue() {
      if (this.issue?.close_blockers) { this.closeRefused = true; this.$refs.scrollContainer?.scrollTo({ top: 0, behavior: 'smooth' }); return }
      this.openModal('close')
    },
    closeBlockerText() {
      const live = this.issue?.close_blockers?.live_orders || []
      const names = this.listWords(live.map(o => o.contractor))
      return live.length === 1 ? `A work order is still in progress: ${names}. Mark it done or cancel it first.`
        : `${live.length} work orders are still in progress: ${names}. Mark them done or cancel them first.`
    },
    // What closing will tidy away, in a line
    closeTidyText() {
      const reqs = this.asks.filter(j => j.kind === 'quote_request' && !j.dead && j.status !== 'completed').length
      const qs = (this.issue?.quotes || []).filter(q => ['requested', 'received', 'with_landlord'].includes(q.status)).length
      if (!reqs && !qs) return ''
      const parts = [reqs ? (reqs === 1 ? '1 quote request' : reqs + ' quote requests') : '', qs ? (qs === 1 ? '1 quote' : qs + ' quotes') : ''].filter(Boolean).join(' and ')
      return parts.charAt(0).toUpperCase() + parts.slice(1) + ' still open will be ' + (reqs && qs ? 'cancelled or declined' : reqs ? 'cancelled' : 'declined') + '.'
    },

    // Photos added from the menu: uploaded, then put on the issue
    async addPhotos(fileList) {
      const files = [...(fileList || [])].filter(f => f.type?.startsWith('image/'))
      if (!files.length) return
      this.photosUploading = true
      try {
        const blobs = await Promise.all(files.map(f => this.uploadFile(f)))
        const data = await this.post(this.base, { signed_blob_ids: blobs.map(b => b.signed_id) }, 'PATCH')
        if (data?.issue) this.issue = data.issue
      } catch (e) {
        window.alert?.(e.message || 'Could not add the photos')
      } finally {
        this.photosUploading = false
      }
    },
    removeDocument(doc) { return this.runVerb(this.base, { remove_document_ids: [doc.id] }, 'PATCH') },

    openModal(name, job = null, quote = null) {
      this.modalJob = job
      this.modalQuote = quote
      const i = this.issue || {}
      const base = { saving: false, error: null, contractor: null, contractor_query: '', contractor_results: [], suggested: [], file: null, uploading: false, analysing: false, signed_blob_id: null }
      const forms = {
        edit: { title: i.title, description: i.description || '', reported_via: i.reported_via },
        invite: { kind: 'work_order', brief: '', response_due_days: 5, access_arrangement: this.defaultAccess(), access_detail: '', send_invitation: true, keep_hold: false,
                  issue_job_id: this.defaultJobChoice(), job_title: '', second_order: false, authorised_by: null, authorisation_note: '', maximum_cost: '',
                  start: 'now', waits_for_job_id: '', not_before: '', deadline: '', photo_ids: this.imageDocuments.map(d => d.id), suggested: this.suggestedContractors() },
        // A change to a work order: what, what it does to the price, who agreed it, and whether the contractor is told
        change: { text: '', price_kind: 'none', amount: '', new_total: '', authorised_by: 'none', authorisation_note: '', notify_contractor: !!(job?.contractor?.email) },
        // The date and a note; the tenants told when the contractor arranges it with them
        book: { date: job?.booking ? job.booking.on.slice(0, 10) : '', time_text: (job?.booking?.time_text || '').trim(), notify_tenants: job?.access_arrangement === 'direct_with_tenant' && (i.tenants || []).length > 0 },
        // A hold on a request, a quote or a work order's job: why, until when, and a job that releases it sooner
        // The emails are ticks: the contractor on it (a quote's contractor is not told by default), and the tenants where a visit was coming
        hold: { reason: '', until: daysFromNow(14), waits_for_job_id: '', hold_job_on: false, notify_contractor: !!((job || quote)?.contractor?.email), notify_tenant: !!(job?.visit_expected && (i.tenants || []).length) },
        // The issue on hold: everything open is offered to be held with it, ticked; every contractor on it offered an email, ticked
        hold_issue: { reason: '', until: daysFromNow(14), quote_request_ids: (i.holdable?.quote_requests || []).map(x => x.id), quote_ids: (i.holdable?.quotes || []).map(x => x.id), job_ids: (i.holdable?.jobs || []).map(x => x.id),
                      notify_contractor_ids: (i.hold_notices?.contractors || []).map(x => x.id), notify_tenant: !!(i.hold_notices?.tenant && (i.tenants || []).length) },
        // Off hold: the same ticks, for the issue (whoever was held with it) or for one thing
        release: job || quote ? { notify_contractor: !!((job || quote)?.contractor?.email), notify_tenant: !!(job?.visit_expected && (i.tenants || []).length) }
                              : { notify_contractor_ids: (i.release_notices?.contractors || []).map(x => x.id), notify_tenant: !!(i.release_notices?.tenant && (i.tenants || []).length) },
        decline_quote: { reason: '', notify_contractor: !!(quote?.contractor?.email) },
        // The way in changes after the ask has gone
        access: { access_arrangement: job?.access_arrangement || 'direct_with_tenant', access_detail: job?.access_detail || '', notify_contractor: !!(job?.contractor?.email) },
        complete: { notes: '' },
        quote: { reference: '', valid_until: '', notes: '', lines: [{ description: '', amount: '' }], vat: false, suggested: this.suggestedContractors(),
                 issue_job_id: this.defaultJobChoice(), job_title: '' },
        approve: (() => {
          const r = quote ? this.rivalsFor(quote) : { quotes: [], requests: [] }
          // A revised price from the contractor who holds the job's work order is applied as a change to it unless told otherwise
          // The way in for the work starts as the request had it
          const request = quote && (i.quote_requests || []).find(x => x.id === quote.quote_request_id)
          return { issue_job_id: quote ? String(quote.issue_job_id) : null, decline_quote_ids: r.quotes.map(x => x.id), cancel_quote_request_ids: r.requests.map(x => x.id), second_order: false,
                   authorised_by: 'landlord', words: '', as_change: !!quote?.revises_work_order_id, start: 'now', waits_for_job_id: '', not_before: '', deadline: '', notify_rivals: true, keep_hold: false,
                   access_arrangement: request?.access_arrangement || this.defaultAccess(), access_detail: request?.access_detail || '' }
        })(),
        invoice: { gross_amount: '', vat: false, invoice_reference: '', invoice_date: today(), description: i.title || '', mark_complete: true, record_only: false, duplicate: null, acknowledge_duplicate: false, suggested: this.suggestedContractors() },
        // A request is cancelled by us, a reason given; a work order may end on their word or ours
        call_off: { by: 'agent', reason: '', notify_contractor: !!(job?.contractor?.email) },
        send_quotes: {},
        close: { reason: '' },
        certificate: { issue_date: today(), expiry_date: this.certificateExpiry(today()), expiry_auto: true, certificate_number: '', result: '',
                       warnings: [], send_to_landlords: true, send_to_tenants: (i.tenants || []).length > 0 }
      }
      this.form = { ...base, ...(forms[name] || {}) }
      if ((name === 'quote' || name === 'invoice') && this.form.suggested.length === 1) this.form.contractor = this.form.suggested[0]
      // A price recorded against a visit is for that visit's job
      if (name === 'quote' && job?.issue_job_id) this.form.issue_job_id = String(job.issue_job_id)
      this.modal.open = name
    },

    closeModal() { this.modal.open = null },

    // A tick in a list of ids: in or out. The lists are bound by hand
    // because a checkbox's value binding is unreliable inside the modal
    // templates, so x-model saw every tick as unset
    toggleId(list, id) {
      const at = list.indexOf(id)
      at === -1 ? list.push(id) : list.splice(at, 1)
    },

    // The contractor a modal's email tick names: the quote's or the ask's, only with an address
    noticeContractor() {
      const c = this.modalQuote?.contractor || this.modalJob?.contractor
      return c && c.email ? c : null
    },
    releaseText() {
      if (this.modalQuote) return 'The ' + (this.modalQuote.amount_display || '') + ' quote from ' + this.modalQuote.contractor.name + ' is back waiting for a decision.'
      if (this.modalJob) return (this.modalJob.contractor ? this.modalJob.contractor.name : 'The contractor') + "'s " + (this.modalJob.kind === 'quote_request' ? 'quote request' : 'work order') + ' is back on.'
      return 'The issue comes off hold, and with it whatever was held with it.'
    },

    suggestedContractors() {
      const seen = new Set()
      return this.asks.filter(j => j.contractor && !j.dead).map(j => j.contractor).filter(c => !seen.has(c.id) && seen.add(c.id))
                 .map(c => ({ id: c.id, display_name: c.name, name: c.name, email: c.email }))
    },

    modalTitle(name) {
      const who = (this.modalQuote || this.modalJob)?.contractor?.name
      const request = this.modalJob?.kind === 'quote_request'
      return { approve: `Accept ${who || 'this'}'s quote`, edit: 'Edit issue', invite: { quote_request: 'Request a quote', work_order: 'Create work order' }[this.form.kind] || 'Ask a contractor', book: request ? 'Book the visit' : 'Set the booked date',
               hold: this.modalQuote ? 'Put the quote on hold' : (request ? 'Put the request on hold' : 'Put the work order on hold'), hold_issue: 'Put the issue on hold',
               release: this.modalQuote ? 'Resume the quote' : (this.modalJob ? (request ? 'Resume the quote request' : 'Resume the work order') : 'Take the issue off hold'),
               decline_quote: `Decline ${who || 'this'}'s quote`, access: 'Change access', change: 'Change work order', complete: 'Mark the work as complete', quote: 'Record a quote', invoice: 'Add an invoice',
               call_off: (this.modalJob?.status === 'invited' ? 'Withdraw the ' : 'Cancel the ') + (request ? 'quote request' : 'work order'), send_quotes: 'Send to the landlord',
               close: 'Close the issue', certificate: 'Add certificate' }[name]
    },

    modalSubmitLabel(name) {
      const request = this.modalJob?.kind === 'quote_request'
      const n = this.openQuotes.length
      return { approve: 'Write the approval email', edit: 'Save', invite: this.form.send_invitation === false ? { quote_request: 'Record the request', work_order: 'Record the work order' }[this.form.kind] || 'Record it' : { quote_request: 'Ask for a quote', work_order: 'Create work order' }[this.form.kind] || 'Send',
               book: 'Save date', hold: 'Put on hold', hold_issue: 'Put on hold', release: this.modalJob || this.modalQuote ? 'Resume' : 'Take off hold', decline_quote: 'Decline quote', access: 'Save access', change: 'Save change', complete: 'Mark as complete',
               quote: 'Record quote', invoice: this.form.record_only ? 'Record invoice' : 'Add invoice', send_quotes: n === 2 ? 'Yes, send both' : `Yes, send all ${n}`,
               call_off: (this.modalJob?.status === 'invited' ? 'Withdraw ' : 'Cancel ') + (request ? 'quote request' : 'work order'), close: 'Close issue', certificate: 'Add certificate' }[name]
    },

    // Where a form ends something, Cancel would read both ways: it keeps it
    modalCancelLabel(name) {
      return ['call_off', 'decline_quote'].includes(name) ? 'Keep it' : 'Cancel'
    },

    // Every form's footer asks, open or not; only the open form's answer counts
    modalReady(name) {
      if (name !== this.modal.open) return false
      const f = this.form
      switch (name) {
        case 'edit': return !!(f.title || '').trim()
        case 'invite': return !!f.contractor && !!(f.brief || '').trim() && (f.access_arrangement !== 'other' || !!(f.access_detail || '').trim()) && (this.instructingDraft() || (this.jobChoiceReady() && (!this.secondOrderWarning() || f.second_order)))
        case 'approve': return (f.as_change || !this.secondOrderWarning() || f.second_order) && (f.as_change || f.access_arrangement !== 'other' || !!(f.access_detail || '').trim())
        case 'change': return !!(f.text || '').trim() && (f.price_kind === 'none' || ['landlord', 'us'].includes(f.authorised_by)) && (f.price_kind !== 'add' || f.amount !== '') && (f.price_kind !== 'total' || f.new_total !== '')
        // No date saves only where there was one: it clears the booking
        case 'book': return !!f.date || !!this.modalJob?.booking
        case 'hold': case 'hold_issue': return !!(f.reason || '').trim() && !!f.until
        case 'release': case 'decline_quote': case 'send_quotes': return true
        case 'access': return f.access_arrangement !== 'other' || !!(f.access_detail || '').trim()
        case 'complete': return true
        case 'certificate': return !!f.signed_blob_id && !!f.issue_date && (!!f.expiry_date || f.result === 'unsatisfactory') && !f.uploading && !f.analysing && (!this.issue?.renewal?.has_result || !!f.result)
        case 'quote': return !!f.contractor && f.lines.some(l => (l.description || '').trim() && l.amount !== '' && l.amount != null) && this.jobChoiceReady() && !f.uploading && !f.analysing
        case 'invoice': return !!f.contractor && parseFloat(f.gross_amount) > 0 && !f.uploading && !f.analysing
        case 'call_off': return !!f.by
        case 'close': return true
        default: return false
      }
    },

    // Record a quote's running figures: the lines, the VAT on top when ticked, the total passed on
    get quoteRunning() {
      const net = (this.form.lines || []).reduce((t, l) => t + (parseFloat(l.amount) || 0), 0)
      const vat = this.form.vat ? Math.round(net * 20) / 100 : 0
      return { net, vat, total: net + vat }
    },
    // Lines read off a PDF or an email: a quantity and a price each, folded into one figure
    readLines(lines) {
      return lines.map(l => {
        const qty = parseFloat(l.quantity) || 1
        const amount = Math.round(qty * (parseFloat(l.unit_price) || 0) * 100) / 100
        return { description: (qty !== 1 ? `${qty} × ` : '') + (l.description || ''), amount: amount ? String(amount) : '' }
      })
    },

    async searchContractors() {
      const q = (this.form.contractor_query || '').trim()
      if (q.length < 2) { this.form.contractor_results = []; return }
      try {
        const response = await fetch(`${this.options.contractors_url || '/contractors.json'}?q=${encodeURIComponent(q)}&limit=10`, { headers: { 'Accept': 'application/json' } })
        const data = await response.json()
        this.form.contractor_results = (Array.isArray(data) ? data : data.contractors || []).slice(0, 10)
      } catch (e) {
        this.form.contractor_results = []
      }
    },

    async post(url, body, method = 'POST') {
      const response = await fetch(url, {
        method, headers: { 'Accept': 'application/json', 'Content-Type': 'application/json', 'X-CSRF-Token': csrf() }, body: JSON.stringify(body)
      })
      const data = await response.json().catch(() => ({}))
      if (!response.ok || data.success === false) { const err = new Error(data.error || 'Something went wrong'); err.data = data; throw err }
      return data
    },

    async submitModal() {
      const name = this.modal.open
      const f = this.form
      this.form.saving = true
      this.form.error = null
      try {
        let data
        switch (name) {
          case 'edit':
            data = await this.post(`${this.base}`, { issue: { title: f.title, description: f.description, reported_via: f.reported_via } }, 'PATCH')
            break
          case 'invite':
            data = await this.post(`${this.base}/${f.kind === 'quote_request' ? 'quote_requests' : 'work_orders'}`, { contractor_id: f.contractor.id, brief: f.brief.trim(), response_due_days: f.response_due_days, access_arrangement: f.access_arrangement, send_invitation: f.send_invitation, photo_ids: f.photo_ids, access_detail: f.access_detail, keep_hold: f.keep_hold, ...this.jobParams(),
                                   ...(f.kind === 'work_order' ? { authorised_by: f.authorised_by, authorisation_note: f.authorisation_note, maximum_cost: f.maximum_cost || null, ...(this.instructingDraft() ? {} : this.startParams(f)) } : {}) })
            break
          case 'approve':
            // The approval goes through the composer; sending it is what accepts the quote, with the ticked rivals set aside
            // The email gives the way in and the start chosen here, as the work order will carry them
            this.closeModal()
            const start = this.startParams(f)
            const terms = f.as_change ? '' : `?${new URLSearchParams({ access_arrangement: f.access_arrangement || '', access_detail: f.access_detail || '', start: f.start || 'now',
                                                                     waits_for_job_id: start.waits_for_job_id || '', not_before: start.not_before || '', deadline: start.deadline || '' })}`
            return await this.openDraftInComposer(`${this.workBase}/quotes/${this.modalQuote.id}/approval_email_draft${terms}`,
                                                  { decline_quote_ids: f.decline_quote_ids, cancel_quote_request_ids: f.cancel_quote_request_ids, second_order: f.second_order,
                                                    authorised_by: f.authorised_by, words: f.words, as_change: f.as_change, notify_rivals: f.notify_rivals, keep_hold: f.keep_hold,
                                                    ...(f.as_change ? {} : { access_arrangement: f.access_arrangement, access_detail: f.access_detail, ...this.startParams(f) }) })
          case 'change':
            data = await this.post(`${this.askPath(this.modalJob)}/changes`, { text: f.text, price_kind: f.price_kind, amount: f.amount || null, new_total: f.new_total || null,
                                                                                authorised_by: f.authorised_by === 'none' ? null : f.authorised_by, authorisation_note: f.authorisation_note, notify_contractor: f.notify_contractor })
            break
          case 'book':
            // Saved with no date: the date is off and no new one is known, so the booking goes and the ask waits for a date. Nobody is emailed
            data = f.date ? await this.post(`${this.askPath(this.modalJob)}/book`, { on: `${f.date}T${this.timeFrom(f.time_text)}`, time_text: f.time_text, notify_tenants: f.notify_tenants }, 'PATCH')
                          : await this.post(`${this.askPath(this.modalJob)}/unbook`, {}, 'PATCH')
            break
          case 'hold':
            // A quote's hold, or an ask's (a work order holds its job)
            data = await this.post(this.modalQuote ? `${this.workBase}/quotes/${this.modalQuote.id}/hold` : `${this.askPath(this.modalJob)}/hold`, { reason: f.reason, until: f.until, waits_for_job_id: (f.hold_job_on && f.waits_for_job_id) || null, notify_contractor: f.notify_contractor, notify_tenant: f.notify_tenant })
            break
          case 'hold_issue':
            data = await this.post(`${this.base}/hold`, { reason: f.reason, until: f.until, quote_request_ids: f.quote_request_ids, quote_ids: f.quote_ids, job_ids: f.job_ids, notify_contractor_ids: f.notify_contractor_ids, notify_tenant: f.notify_tenant })
            break
          case 'release':
            data = await this.post(this.modalQuote ? `${this.workBase}/quotes/${this.modalQuote.id}/hold` : (this.modalJob ? `${this.askPath(this.modalJob)}/hold` : `${this.base}/hold`),
                                   this.modalJob || this.modalQuote ? { notify_contractor: f.notify_contractor, notify_tenant: f.notify_tenant } : { notify_contractor_ids: f.notify_contractor_ids, notify_tenant: f.notify_tenant }, 'DELETE')
            break
          case 'decline_quote':
            data = await this.post(`${this.workBase}/quotes/${this.modalQuote.id}/decline`, { reason: f.reason, notify_contractor: f.notify_contractor })
            break
          case 'access':
            data = await this.post(`${this.askPath(this.modalJob)}/access`, { access_arrangement: f.access_arrangement, access_detail: f.access_detail, notify_contractor: f.notify_contractor }, 'PATCH')
            break
          case 'complete':
            data = await this.post(`${this.askPath(this.modalJob)}/complete`, { notes: f.notes }, 'PATCH')
            break
          case 'quote':
            data = await this.post(`${this.base}/quotes`, { contractor_id: f.contractor.id, lines: f.lines.filter(l => (l.description || '').trim()).map(l => ({ description: l.description.trim(), quantity: 1, unit_price: parseFloat(l.amount) || 0 })),
                                                            vat_rate: f.vat ? 0.2 : 0, reference: f.reference, valid_until: f.valid_until || null, notes: f.notes, signed_blob_id: f.signed_blob_id, email_attachment_id: f.email_attachment_id || null, email_thread_id: f.email_thread_id || null,
                                                            proposed_action_id: this.proposedActionId || null, quote_request_id: (this.modalJob?.kind === 'quote_request' ? this.modalJob.id : null), ...this.jobParams() })
            this.proposedActionId = null
            break
          case 'invoice':
            return await this.submitInvoice()
          case 'call_off':
            data = await this.post(`${this.askPath(this.modalJob)}/call_off`, { by: f.by, reason: f.reason, notify_contractor: f.by === 'agent' && f.notify_contractor })
            break
          case 'send_quotes':
            return await this.sendQuotes('all')
          case 'close':
            data = await this.post(`${this.base}/close`, { reason: f.reason }, 'PATCH')
            break
          case 'certificate':
            data = await this.post(`${this.base}/certificates`, { signed_blob_id: f.signed_blob_id, issue_date: f.issue_date, expiry_date: f.expiry_date, certificate_number: f.certificate_number, result: f.result || null,
                                                                  send_to_landlords: f.send_to_landlords, send_to_tenants: f.send_to_tenants })
            break
        }
        if (data?.issue) this.issue = data.issue
        this.closeModal()
      } catch (e) {
        this.form.error = e.message
      } finally {
        this.form.saving = false
      }
    },

    // The job an invite or a quote is for: an existing one by id, or a new one by its title
    jobParams() {
      if (this.instructingDraft()) return {}
      const f = this.form
      return f.issue_job_id && f.issue_job_id !== '__new' ? { issue_job_id: f.issue_job_id, second_order: !!f.second_order } : { job_title: (f.job_title || '').trim(), second_order: false }
    },

    async submitInvoice() {
      const f = this.form
      this.form.saving = true
      this.form.error = null
      this.form.duplicate = null
      try {
        const data = await this.post(`${this.base}/invoices`, {
          contractor_id: f.contractor.id, gross_amount: this.invoiceGross(), invoice_reference: f.invoice_reference, invoice_date: f.invoice_date || null,
          description: f.description, mode: f.record_only ? 'record_only' : 'charge', mark_complete: f.mark_complete, acknowledge_duplicate: f.acknowledge_duplicate, signed_blob_id: f.signed_blob_id,
          email_attachment_id: f.email_attachment_id || null, proposed_action_id: this.proposedActionId || null
        })
        this.proposedActionId = null
        this.issue = data.issue
        this.closeModal()
      } catch (e) {
        if (e.data?.duplicate) this.form.duplicate = e.message
        else this.form.error = e.message
      } finally {
        this.form.saving = false
      }
    },

    // "morning" / "after 2pm" / "12 to 5": the first hour named, else 09:00
    timeFrom(text) {
      const m = (text || '').match(/(\d{1,2})(?::(\d{2}))?\s*(am|pm)?/i)
      if (!m) return /afternoon|evening/i.test(text || '') ? '13:00' : '09:00'
      let h = parseInt(m[1], 10)
      if (m[3]?.toLowerCase() === 'pm' && h < 12) h += 12
      return `${String(h).padStart(2, '0')}:${m[2] || '00'}`
    },

    async uploadFile(file) {
      const { DirectUpload } = await import('@rails/activestorage')
      return new Promise((resolve, reject) => {
        new DirectUpload(file, '/rails/active_storage/direct_uploads').create((error, blob) => error ? reject(error) : resolve(blob))
      })
    },

    // The new certificate's PDF: uploaded straight away and read, as the compliance modal reads one, to fill in
    // its dates, number and result; filed on save. Unread, the form stays as it was, to fill in by hand
    async uploadCertificateFile(file) {
      if (!file) return
      const form = this.form
      form.file = file
      form.uploading = true
      form.error = null
      form.warnings = []
      try {
        const blob = await this.uploadFile(file)
        form.signed_blob_id = blob.signed_id
      } catch (e) {
        form.error = 'Could not upload the file'
        return
      } finally {
        form.uploading = false
      }
      form.analysing = true
      try {
        const data = await this.post(`${this.base}/certificates/analyse`, { signed_blob_id: form.signed_blob_id })
        // Another file dropped meanwhile, or the modal shut: this reading is for nothing on screen
        if (this.form !== form || form.file !== file) return
        this.applyCertificateReading(data.extracted || {})
        form.warnings = data.warnings || []
      } catch (e) {
        // Unread: nothing to say, the fields are there to fill in
      } finally {
        form.analysing = false
      }
    },

    // What was read off it, where the reader recognised a certificate. An expiry under 60 days after the
    // issue is a misreading (no certificate lasts so short), so the type's own term stands instead
    applyCertificateReading(x) {
      if (!x.certificate_type || x.certificate_type === 'unknown' || x.confidence === 'low') return
      if (x.issue_date) this.form.issue_date = x.issue_date
      const days = x.expiry_date && x.issue_date ? (new Date(x.expiry_date) - new Date(x.issue_date)) / 86400000 : null
      if (x.expiry_date && (days === null || days >= 60)) {
        this.form.expiry_date = x.expiry_date
        this.form.expiry_auto = false
      } else this.certificateIssued()
      if (x.certificate_number) this.form.certificate_number = x.certificate_number
      if (this.issue?.renewal?.has_result && ['satisfactory', 'unsatisfactory'].includes(x.outcome)) this.form.result = x.outcome
    },

    async analyseQuoteFile(file) { return this.analyseFile(file, 'quotes') },
    async analyseInvoiceFile(file) { return this.analyseFile(file, 'invoices') },

    async analyseFile(file, kind) {
      if (!file) return
      this.form.file = file
      this.form.uploading = true
      this.form.error = null
      try {
        const blob = await this.uploadFile(file)
        this.form.signed_blob_id = blob.signed_id
        this.form.uploading = false
        this.form.analysing = true
        const data = await this.post(`${this.base}/${kind}/analyse`, { signed_blob_id: blob.signed_id })
        const x = data.extracted || {}
        if (kind === 'quotes') {
          this.form.reference = x.reference || this.form.reference
          this.form.valid_until = x.valid_until || this.form.valid_until
          this.form.notes = x.notes || this.form.notes
          if (Array.isArray(x.lines) && x.lines.length) this.form.lines = this.readLines(x.lines)
          if (x.vat_rate !== undefined && x.vat_rate !== null) this.form.vat = parseFloat(x.vat_rate) > 0
        } else {
          const gross = (parseFloat(x.net_amount) || 0) + (parseFloat(x.vat_amount) || 0)
          if (gross > 0) this.form.gross_amount = gross.toFixed(2)
          this.form.invoice_reference = x.invoice_reference || this.form.invoice_reference
          this.form.invoice_date = x.invoice_date || this.form.invoice_date
          this.form.description = x.description || this.form.description
        }
        if (data.matched_contractors?.length === 1 && !this.form.contractor) this.form.contractor = { id: data.matched_contractors[0].id, display_name: data.matched_contractors[0].name, name: data.matched_contractors[0].name }
        else if (data.matched_contractors?.length > 1) this.form.suggested = data.matched_contractors.map(c => ({ id: c.id, display_name: c.name, name: c.name }))
      } catch (e) {
        this.form.error = e.message.includes('read') ? null : e.message
      } finally {
        this.form.uploading = false
        this.form.analysing = false
      }
    },

    // The spinner's words while a dropped quote, invoice or certificate uploads, then while it is read
    readingWords(name) {
      const noun = { quote: 'quote', invoice: 'invoice', certificate: 'certificate' }[name] || 'document'
      if (this.form.uploading) return { title: `Uploading the ${noun}…`, detail: this.form.file?.name || '' }
      const detail = { quote: 'Filling in who it is from, its lines and the VAT', invoice: 'Filling in who it is from, the amount and the date',
                       certificate: this.issue?.renewal?.has_result ? 'Filling in its dates, number and result' : 'Filling in its dates and number' }[name]
      return { title: `Reading the ${noun}…`, detail: detail || '' }
    },

    // --- The verbs: one click -----------------------------------------------

    async runVerb(url, body = {}, method = 'POST') {
      try {
        const data = await this.post(url, body, method)
        if (data.issue) this.issue = data.issue
      } catch (e) {
        this.error = null
        window.alert?.(e.message)
      }
    },

    chaseJob(job) { return this.runVerb(`${this.askPath(job)}/chase`) },
    // Back as it was: the issue reopened, a cancelled work order reopened, a request or a declined quote reinstated
    reopenIssue() { return this.runVerb(`${this.base}/reopen`, {}, 'PATCH') },
    reopenOrder(job) { return this.runVerb(`${this.askPath(job)}/reopen`) },
    reinstateRequest(job) { return this.runVerb(`${this.askPath(job)}/reinstate`) },
    // Off hold: the issue (and whatever was held with it), a request or work order, a quote; each asks who to tell
    releaseIssueHold() { this.openModal('release') },
    releaseAskHold(job) { this.openModal('release', job) },
    releaseQuoteHold(q) { this.openModal('release', null, q) },
    // The picker wants a contractor record shape; the payload's is the display shape
    contractorRecord(c) { return c ? { id: c.id, display_name: c.name, name: c.name, email: c.email, phone: c.phone } : null },
    acceptJob(job) { return this.runVerb(`${this.askPath(job)}/accept`) },
    // Declining asks why, and whether the contractor is thanked
    declineQuote(q) { this.openModal('decline_quote', null, q) },
    // The price from a visit, recorded against it
    addQuoteFor(job) {
      this.openModal('quote', job)
      if (job.contractor) this.form.contractor = this.contractorRecord(job.contractor)
    },
    // Accepting: the form offers the job's other quotes and requests for decline, each ticked by the user,
    // and warns when the job already has a live work order. Then the approval email is written.
    approveQuote(q) { this.openModal('approve', null, q) },

    // The two that go through the composer: the draft opens there, and
    // sending is what records the send or the approval.
    // The quotes email: every quote waiting for the landlords' decision in one email, the new ones first and the
    // ones sent before set out again. Sending records a send on each quote.
    async sendQuotesToLandlords() { return this.openDraftInComposer(`${this.base}/quotes_email_draft`) },

    // payload: what the send's completion carries beyond the thread (the acceptance's ticks)
    async openDraftInComposer(url, payload = null) {
      try {
        const response = await fetch(url, { headers: { 'Accept': 'application/json' } })
        const data = await response.json().catch(() => ({}))
        if (!response.ok || !data.draft) throw new Error(data.error || 'Could not draft the email')
        const draft = data.draft
        const completion = payload && draft.completion ? { ...draft.completion, payload: { ...(draft.completion.payload || {}), ...payload } } : draft.completion
        window.dispatchEvent(new CustomEvent('open-email-compose', {
          detail: { to: draft.recipients, subject: draft.subject, body_html: draft.body_html, attachments: draft.attachments || [], completion, about: draft.about }
        }))
      } catch (e) {
        window.alert?.(e.message)
      }
    },

    // --- Quotes: requests waiting for a price, the quotes in, the dead folded away ---

    // Every card, ranked as the tracker reads them: quotes waiting for a decision,
    // then requests out and quotes on hold, then accepted quotes; dead ones fold away.
    // A request whose quote has come is the quote's card from then on
    get quoteCards() {
      const i = this.issue
      if (!i) return []
      const cards = []
      ;(i.quote_requests || []).forEach(r => {
        if ((r.priced_quotes || []).length) return
        cards.push({ key: 'r' + r.id, kind: 'request', rec: r, rank: r.dead || r.status === 'completed' ? 3 : 1, at: r.created_at || '' })
      })
      ;(i.quotes || []).forEach(q => {
        const live = ['received', 'with_landlord'].includes(q.status)
        cards.push({ key: 'q' + q.id, kind: 'quote', rec: q, rank: q.status === 'approved' ? 2 : live ? (q.hold ? 1 : 0) : 3, at: q.received_at || '' })
      })
      return cards.sort((a, b) => a.rank - b.rank || a.at.localeCompare(b.at))
    },
    get liveQuoteCards() { return this.quoteCards.filter(c => c.rank < 3) },
    get deadQuoteCards() { return this.quoteCards.filter(c => c.rank === 3) },
    // The quotes waiting for a decision, not held: what a quotes email carries
    get openQuotes() { return (this.issue?.quotes || []).filter(q => ['received', 'with_landlord'].includes(q.status) && !q.hold) },
    // The requests still out with no quote back
    waitingRequests() { return this.asks.filter(j => j.kind === 'quote_request' && !j.dead && j.status !== 'completed' && !(j.priced_quotes || []).length) },

    toggleOpen(key) { this.openKeys = { ...this.openKeys, [key]: !this.openKeys[key] } },
    isOpenKey(key) { return !!this.openKeys[key] },

    capitalise(t) { return t ? t.charAt(0).toUpperCase() + t.slice(1) : '' },
    workTitle(id) { return this.workById(id)?.title || this.issue?.work_title || this.issue?.title || '' },
    // "via the tenant" as a card says it: "Via the tenant", "Through us"
    accessWords(text) { return this.capitalise(text || '') },
    // The tenants, for an email tick: "James Smith, the tenant"
    tenantWords() {
      const t = this.issue?.tenants || []
      return t.length ? this.listWords(t.map(x => x.name)) + (t.length > 1 ? ', the tenants' : ', the tenant') : 'the tenant'
    },
    // Under "The tenant": who they are, and that LetAdmin tells them to expect the call
    tenantAccessWords() {
      const t = this.issue?.tenants || []
      return t.map(x => x.name + (x.phone ? ', ' + x.phone : '')).join('; ').replace(/[.\s]+$/, '') + '. Told to expect their call once they are on the job.'
    },
    defaultAccess() { return (this.issue?.tenants || []).length ? 'direct_with_tenant' : 'through_agent' },
    // "Off hold Wed 14 Oct 2026, or sooner once X is complete: reason"
    holdWords(h) {
      if (!h) return ''
      const w = h.waits_for_job_id ? this.workById(h.waits_for_job_id)?.title : null
      return (h.due ? 'Due off hold ' : 'Off hold ') + this.dayWords(h.until) + (w ? ', or sooner once ' + w + ' is complete' : '') + (h.reason ? ': ' + h.reason : '')
    },
    // The job a hold form is about, so it is not offered as the one to wait for
    holdJobId() { return (this.modalQuote || this.modalJob)?.issue_job_id },
    // A request's visit: coming, or been with no quote yet
    visitWords(j) {
      const b = j.booking
      if (!b) return ''
      return `${b.passed ? 'Visited' : 'Visiting'} ${this.dayWords(b.on)}, ${this.relDay(b.on)}${(b.time_text || '').trim() ? ', ' + b.time_text.trim() : ''}${b.passed ? ', no quote yet' : ''}`
    },
    // The invitation still unanswered: when it went and the date to reply by
    inviteWords(j) {
      if (j.status !== 'invited') return ''
      if (this.inviteLate(j)) return `No reply, due ${this.dayWords(j.reply_by)}`
      return `Invited ${this.shortDate(j.sent_at)}${j.reply_by ? ', reply by ' + this.dayWords(j.reply_by) : ''}`
    },
    inviteLate(j) { return j.status === 'invited' && !!j.reply_by && this.dayDiff(j.reply_by) < 0 },
    askedWords(j) {
      return [j.sent_at ? this.shortDate(j.sent_at) : 'Not sent', j.last_chased_at ? 'reminded ' + this.shortDate(j.last_chased_at) : null, j.photos_sent_text].filter(Boolean).join(', ')
    },
    requestPill(r) { return r.hold ? { label: 'On hold', tone: 'hold' } : r.status === 'invited' ? { label: 'Not yet accepted', tone: 'info' } : { label: 'Waiting for quote', tone: 'info' } },
    quotePill(q) { return q.status === 'approved' ? { label: 'Accepted', tone: 'ok' } : q.hold ? { label: 'On hold', tone: 'hold' } : { label: 'Waiting for decision', tone: 'warn' } },
    quoteExpired(q) { return q.status !== 'approved' && !!q.valid_until && this.dayDiff(q.valid_until) < 0 },
    sentWords(q) {
      const n = (q.sends || []).length
      return n ? `Sent ${q.sends[n - 1].at_display}${n > 1 ? ` (${n} times)` : ''}` : 'Not sent yet'
    },
    quoteLines(q) { return (q.lines || []).map(l => ({ text: (l.quantity !== 1 ? `${+l.quantity} × ` : '') + l.description, amount: l.total })) },
    // What a brief says beyond the job, the report and the ask: the notes for the contractor
    extraNotes(j) {
      let b = j.brief || ''
      ;[this.workById(j.issue_job_id)?.title, this.issue?.work_title, this.issue?.title, this.issue?.description, ...Object.values(this.ASK_LINES), ...this.RETIRED_ASK_LINES]
        .filter(Boolean).forEach(t => { b = b.split(t).join('') })
      return b.replace(/\n{3,}/g, '\n\n').trim()
    },
    deadRequestPill(r) { return { cancelled: 'Cancelled', withdrawn: 'Withdrawn', declined: 'Declined', expired: 'No reply', completed: 'No quote' }[r.status] || r.status_label },
    deadRequestWords(r) {
      const how = { cancelled: 'Cancelled', withdrawn: 'Withdrawn', declined: 'They declined', expired: 'No reply', completed: 'Visited' }[r.status] || r.status_label
      const when = r.cancelled_at_display || r.completed_at_display || r.responded_at_display
      const why = r.cancelled_automatically ? 'the issue was closed' : (r.ended_because || r.outcome_label || r.completion_notes)
      return `No quote. ${how}${when ? ' ' + when : ''}${why ? ': ' + why : ''}.`
    },
    deadQuotePill(q) { return { declined: 'Declined', expired: 'Expired', withdrawn: 'Cancelled', superseded: 'Superseded', requested: 'No quote' }[q.status] || q.status_label },
    deadQuoteWords(q) {
      if (q.status === 'requested' || q.status === 'withdrawn') return `No quote. Asked ${q.requested_at_display || ''}${q.cancellation_reason ? ': ' + q.cancellation_reason : ''}.`
      if (q.status === 'declined') return `Declined${q.declined_at_display ? ' ' + q.declined_at_display : ''}${q.decline_reason ? ': ' + q.decline_reason : ''}.`
      return this.deadQuotePill(q) + '.'
    },
    // A declined quote can still be accepted while nobody holds the job's work order
    canAcceptAfterAll(q) { return this.isActive && q.status === 'declined' && !this.workById(q.issue_job_id)?.live_order },
    acceptFootnote() {
      const q = this.modalQuote
      if (!q) return ''
      const rivals = this.rivalsFor(q)
      const then = ' The approval email opens next, for you to check; sending it is what accepts the quote.'
      if (this.form.as_change) return `${q.contractor.name}'s work order changes to this price.` + then
      return `${q.contractor.name} will be instructed and this becomes a work order.` + (rivals.quotes.length || rivals.requests.length ? ' Anything left unticked above stays open.' : '') + then
    },
    sendQuotesReminderText() {
      const sent = this.openQuotes.filter(q => (q.sends || []).length), fresh = this.openQuotes.filter(q => !(q.sends || []).length)
      if (!sent.length || !fresh.length) return ''
      return `${this.listWords(sent.map(q => q.contractor.name))} ${sent.length === 1 ? 'has' : 'have'} already gone to the landlord. The email will lead with the new ${fresh.length === 1 ? 'quote' : 'quotes'} and repeat the earlier ${sent.length === 1 ? 'one' : 'ones'} as a reminder.`
    },

    // Send to landlord: with more than one quote waiting, all of them or just this one
    sendToLandlord(q) {
      if (this.openQuotes.length > 1) return this.openModal('send_quotes', null, q)
      return this.openDraftInComposer(`${this.base}/quotes_email_draft?quote_ids[]=${q.id}`)
    },
    async sendQuotes(which) {
      const q = this.modalQuote
      this.closeModal()
      return which === 'all' ? this.sendQuotesToLandlords() : this.openDraftInComposer(`${this.base}/quotes_email_draft?quote_ids[]=${q.id}`)
    },
    // A declined quote back waiting for a decision, and the accept form open on it
    async acceptAfterAll(q) {
      try {
        const data = await this.post(`${this.workBase}/quotes/${q.id}/reinstate`, {})
        if (data.issue) this.issue = data.issue
        const back = (this.issue?.quotes || []).find(x => x.id === q.id)
        if (back) this.approveQuote(back)
      } catch (e) {
        window.alert?.(e.message)
      }
    },
    // The quote as sent, attached after it was recorded
    async attachQuoteDocument(q, file) {
      if (!file) return
      try {
        const blob = await this.uploadFile(file)
        const data = await this.post(`${this.workBase}/quotes/${q.id}/document`, { signed_blob_id: blob.signed_id })
        if (data.issue) this.issue = data.issue
      } catch (e) {
        window.alert?.(e.message)
      }
    },

    // --- Work orders --------------------------------------------------------

    // Live orders first, then those waiting to start, on hold, complete; cancelled ones fold away.
    // A renewal's draft, with nobody on it, is not a work order
    get orderCards() {
      return (this.issue?.work_orders || []).filter(j => j.status !== 'draft').map(j => {
        const rank = j.dead ? 9 : j.status === 'completed' ? 3 : j.hold ? 2 : (j.start && (j.start.waiting || j.start.not_before_future)) ? 1 : 0
        return { key: 'w' + j.id, kind: 'order', rec: j, rank, at: j.created_at || '' }
      }).sort((a, b) => a.rank - b.rank || a.at.localeCompare(b.at))
    },
    get liveOrderCards() { return this.orderCards.filter(c => c.rank < 9) },
    get deadOrderCards() { return this.orderCards.filter(c => c.rank === 9) },
    orderPill(j) {
      if (j.status === 'completed') return { label: 'Complete', tone: 'ok' }
      if (j.hold) return { label: 'On hold', tone: 'hold' }
      if (j.status === 'invited') return { label: 'Not yet accepted', tone: 'info' }
      if (j.start?.waiting) return { label: 'After: ' + (j.start.waits_for_title || 'another job'), tone: 'info' }
      if (j.start?.not_before_future) return { label: 'Not before ' + this.dayWords(j.start.not_before), tone: 'info' }
      return { label: 'In progress', tone: 'info' }
    },
    bookedLate(j) { return !!j.booking?.passed && j.status !== 'completed' },
    bookedWords(j) {
      const b = j.booking
      if (!b) return j.status === 'completed' ? 'No date recorded' : 'No date yet'
      const t = (b.time_text || '').trim()
      return `${this.dayWords(b.on)}, ${this.relDay(b.on)}${t ? ', ' + t : ''}${this.bookedLate(j) ? ', not marked complete' : ''}`
    },
    changeWords(c) {
      return `${c.at_display}: ${c.text}${c.amount ? ', ' + c.amount_display : ''}${c.authorisation ? ', authorised by ' + (c.authorisation.by === 'landlord' ? 'the landlord' : 'us') : ''}`
    },
    orderQuote(j) { return j.quote_id ? (this.issue?.quotes || []).find(q => q.id === j.quote_id) || null : null },
    // How the price is made up: the quote's lines (or the maximum given), every change, and where it stands now
    orderPriceLines(j) {
      const q = this.orderQuote(j)
      const out = []
      if (q) {
        this.quoteLines(q).forEach(l => out.push({ text: l.text, amount: this.money(l.amount) }))
        if (!(q.lines || []).length) out.push({ text: 'The accepted quote', amount: this.money(q.amount) })
        if (q.vat_on_top) out.push({ text: 'VAT at ' + Math.round(q.vat_rate * 100) + '%', amount: this.money(q.amount - q.net_total) })
      } else if (j.price?.maximum_cost) {
        out.push({ text: 'Maximum cost when instructed', amount: this.money(j.price.maximum_cost) })
      } else {
        out.push({ text: 'No price when instructed', amount: '' })
      }
      ;(j.changes || []).forEach(c => out.push({ text: `${c.at_display}: ${c.text}`, amount: c.amount ? c.amount_display : 'no change' }))
      if ((j.changes || []).length || q?.vat_on_top) out.push({ text: (j.changes || []).length ? 'Now' : 'Total', amount: this.money(j.price.total), strong: true })
      return out
    },
    deadOrderWords(j) {
      const how = { cancelled: 'Cancelled', withdrawn: 'Withdrawn', declined: 'They declined', expired: 'No reply' }[j.status] || j.status_label
      const when = j.cancelled_at_display || j.responded_at_display
      return `Instructed ${j.sent_at ? this.shortDate(j.sent_at) : this.shortDate(j.created_at)}. ${how}${when ? ' ' + when : ''}${j.ended_because ? ': ' + j.ended_because : ''}.`
    },
    // A change with no effect on the price needs nobody's say-so; one that moves it does
    changePriceKind(kind) {
      this.form.price_kind = kind
      if (kind === 'none') this.form.authorised_by = 'none'
      else if (this.form.authorised_by === 'none') this.form.authorised_by = 'landlord'
    },

    // --- A renewal's certificate -------------------------------------------

    // The certificate that closes the renewal: a satisfactory one (a gas certificate has no result to fail)
    get satisfactoryCertificate() {
      return [...(this.issue?.renewal?.certificates || [])].reverse().find(c => c.result !== 'unsatisfactory') || null
    },
    get renewalExpired() { const e = this.issue?.renewal?.expiry_date; return !!e && this.dayDiff(e) < 0 },

    // The certificate card, in the shape of a work order card. Before any certificate is in, one
    // card says who it comes from: grey until the work is complete, amber once it is ours to get
    get certPending() {
      if (!this.renewal || !this.isActive || (this.issue?.renewal?.certificates || []).length) return null
      const order = this.liveOrderCards.map(c => c.rec).filter(j => j.contractor).pop()
      const done = order && order.status === 'completed'
      return {
        heading: order ? order.contractor.name : 'The new ' + this.certificateWords(),
        pill: done ? { label: 'Waiting', tone: 'warn' } : { label: 'Not in yet', tone: '' },
        fact: done ? 'Work completed ' + order.completed_at_display : (order ? 'Comes in once the work order is complete' : 'Comes in once a contractor has done the work'),
        waiting: !!done
      }
    },
    // Once one is in: the one that stands (the satisfactory one, else the latest that failed), then
    // those it replaced, greyed and folded away as cancelled work orders are
    get liveCertificate() {
      const certs = this.issue?.renewal?.certificates || []
      return this.satisfactoryCertificate || certs[certs.length - 1] || null
    },
    get deadCertificates() {
      return [...(this.issue?.renewal?.certificates || [])].filter(c => c !== this.liveCertificate).reverse()
    },
    certPill(c) {
      if (c.result === 'unsatisfactory') return { label: 'Unsatisfactory', tone: 'warn' }
      return { label: this.issue?.renewal?.has_result ? 'Satisfactory' : 'Filed', tone: 'ok' }
    },
    // Who issued it: the contractor filed with it, else whoever did the work
    certIssuer(c) {
      const done = (this.issue?.work_orders || []).filter(j => j.status === 'completed' && j.contractor)
      return c.issuer || (done.length ? done[done.length - 1].contractor.name : 'The contractor')
    },
    certReplacedWords(c) {
      const certs = this.issue?.renewal?.certificates || []
      const next = certs[certs.indexOf(c) + 1]
      return next ? 'Replaced by the ' + (next.result === 'unsatisfactory' ? '' : 'satisfactory ') + this.certificateWords() + ' of ' + this.shortDate(next.issue_date) : ''
    },
    // The date on the right, where a work order shows its price: how long the new one lasts, or,
    // until a satisfactory one is in, when the current one runs out
    certSlot(c) {
      if (c && c.result !== 'unsatisfactory') return { label: 'valid to', date: c.expiry_date, late: false }
      // After a failed EICR the old expiry no longer counts; the remedial work's limit does
      const by = c && this.issue?.renewal?.remedials_by
      if (by) {
        const late = !this.satisfactoryCertificate && this.dayDiff(by) < 0
        return { label: late ? 'remedial work was due' : 'remedial work due', date: by, late }
      }
      const exp = this.issue?.renewal?.expiry_date
      return exp ? { label: this.renewalExpired ? 'current one expired' : 'current one expires', date: exp, late: this.renewalExpired } : null
    },
    // A new certificate lasts the type's term, less a day: issued 1 Oct 2026, a gas one runs to 30 Sep 2027
    certificateExpiry(issued) {
      const months = this.issue?.renewal?.validity_months || 12
      if (!issued) return ''
      const d = new Date(issued + 'T12:00:00')
      d.setMonth(d.getMonth() + months)
      d.setDate(d.getDate() - 1)
      return d.toISOString().slice(0, 10)
    },
    certificateIssued() { if (this.form.expiry_auto) this.form.expiry_date = this.certificateExpiry(this.form.issue_date) },
    // A failed EICR's remedial work: the 28th day, the inspection day the first (SI 2020/312, reg 3(4))
    remedialsBy(inspected) {
      if (!inspected) return ''
      const d = new Date(inspected + 'T12:00:00')
      d.setDate(d.getDate() + 27)
      return d.toISOString().slice(0, 10)
    },

    // --- Date fields ----------------------------------------------------------

    // Every date in the modals is a flatpickr, as everywhere else: the field holds 2026-10-22 and
    // shows Thu 22 Oct 2026. Run from x-effect, so a date the form sets itself (a document read,
    // an expiry worked out, the modal opening on a booking) shows in the picker too
    datePicker(el, value) {
      let fp = el._flatpickr
      if (!fp) {
        fp = flatpickr(el, { dateFormat: 'Y-m-d', altInput: true, altFormat: 'D j M Y', altInputClass: el.className, disableMobile: true,
          onChange: (_dates, dateStr) => { this.form[el.dataset.date] = dateStr } })
        window.Alpine?.onElRemoved(el, () => fp.destroy())
      }
      if ((fp.input.value || '') !== (value || '')) value ? fp.setDate(value, false) : fp.clear(false)
    },

    // --- Invoices -----------------------------------------------------------

    // Every invoice on the issue, against the work order it was charged to, oldest first; the
    // file attached with it, and whether it is above the price the order stands at
    // Every invoice on the issue, oldest first: who sent it, for which
    // work order, and whether it is above the price that order was accepted at
    get invoiceRows() {
      return this.asks.flatMap(j => (j.invoices || []).map(inv => {
        const accepted = j.price && j.price.total !== null && j.price.total !== undefined ? j.price.total : null
        return { ...inv, key: 'inv' + inv.id, contractor: j.contractor ? j.contractor.name : 'A contractor', job: this.workTitle(j.issue_job_id),
                 accepted, accepted_from: j.price ? j.price.from : null, above: accepted !== null && inv.total > accepted + 0.005 }
      })).sort((a, b) => (a.date || '').localeCompare(b.date || ''))
    },
    // Who an invoice was charged to: the landlord, or each landlord their share
    chargedWords(inv) {
      return inv.charged.length === 1 ? inv.charged[0].name : inv.charged.map(c => c.name + ' ' + this.money(c.amount)).join(' · ')
    },
    // The figure passed on: the amount, with 20% VAT added when ticked
    invoiceGross() {
      const amount = parseFloat(this.form.gross_amount) || 0
      return this.form.vat ? Math.round(amount * 120) / 100 : amount
    },

    // --- Lightbox -----------------------------------------------------------

    openLightbox(docId) {
      const idx = this.imageDocuments.findIndex(d => d.id === docId)
      if (idx < 0) return
      this.lightbox = { open: true, index: idx }
    },
    closeLightbox() { this.lightbox.open = false },
    lightboxNext() { this.lightbox.index = (this.lightbox.index + 1) % this.imageDocuments.length },
    lightboxPrev() { this.lightbox.index = (this.lightbox.index - 1 + this.imageDocuments.length) % this.imageDocuments.length },
    get lightboxCurrentImage() { return this.imageDocuments[this.lightbox.index] || null }
  }
}
